import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Mail, Lock, ArrowRight, Eye, EyeOff, Loader2, Check, Star, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { trackEvent } from '../hooks/useAnalytics';
import SocialButtons from '../components/SocialButtons';
import SignupCount from '../components/SignupCount';
import api from '../lib/api';
import { testimonials } from '../mock';

const Signup = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Modes: 'magic' (default — passwordless email link) | 'password' (classic)
  const [mode, setMode] = useState('magic');

  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast({ title: 'Enter a valid email' });
      return;
    }
    setLoading(true);
    trackEvent('signup_start', { label: 'magic_link' });
    try {
      await api.post('/auth/magic-link', {
        email: email.trim().toLowerCase(),
        name: name.trim() || '',
        source: 'signup_page',
      });
      setMagicSent(true);
      trackEvent('signup_magic_link_sent', { label: 'magic_link' });
    } catch (err) {
      toast({ title: 'Could not send link', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSignup = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: 'Missing fields', description: 'Please enter an email and password.' });
      return;
    }
    if (password.length < 6) {
      toast({ title: 'Weak password', description: 'Use at least 6 characters.' });
      return;
    }
    setLoading(true);
    trackEvent('signup_start', { label: 'email_password' });
    try {
      // Pass display name = email-prefix when blank (we collect business
      // name on the onboarding step now, not here, to keep this form to
      // 2 fields max).
      const displayName = (name || email.split('@')[0] || 'There').trim();
      await signup(displayName, email, password, '', true, true);
      trackEvent('signup_complete', { label: 'email_password' });
      trackEvent('trial_started', { label: 'email_password' });
      toast({ title: 'Welcome! Your 7-day free trial has started.', description: 'No card needed — make your first ad now.' });
      navigate('/onboarding/business');
    } catch (err) {
      toast({ title: 'Sign up failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSocial = (provider) => {
    toast({
      title: `${provider.charAt(0).toUpperCase() + provider.slice(1)} sign-up coming soon`,
      description: 'Please use email for now — it only takes 10 seconds.',
    });
  };

  return (
    <div className="min-h-screen flex bg-[#fdfaf6]">
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="signup-logo-home">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-slate-900">MyBusinessAdPro</span>
          </Link>

          <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">Start free — make your first ad</h1>
          <p className="mt-2 text-slate-600">3 AI ads + 3 AI images on us. No card. No commitment.</p>

          <div className="mt-3">
            <SignupCount testid="signup-page-count" />
          </div>

          <div className="mt-5">
            <SocialButtons onSelect={handleSocial} />
          </div>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">OR EMAIL</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* MAGIC LINK MODE — primary, fewer fields, no password */}
          {mode === 'magic' && !magicSent && (
            <form onSubmit={handleMagicLink} className="space-y-3" data-testid="signup-magic-form">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourbusiness.co.uk"
                    className="pl-10 h-12"
                    data-testid="signup-email-input"
                    autoComplete="email"
                    inputMode="email"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white font-bold rounded-lg group disabled:opacity-70"
                data-testid="signup-magic-submit"
              >
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending link…</>) : (
                  <><Mail className="w-4 h-4 mr-2" /> Email me a sign-in link <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" /></>
                )}
              </Button>
              <p className="text-xs text-slate-500 leading-relaxed">
                By continuing you agree to our
                {' '}<Link to="/legal/terms" target="_blank" className="text-orange-600 font-semibold hover:underline">Terms</Link>,
                {' '}<Link to="/legal/privacy" target="_blank" className="text-orange-600 font-semibold hover:underline">Privacy Policy</Link> and
                {' '}<Link to="/legal/trial-terms" target="_blank" className="text-orange-600 font-semibold hover:underline">Free Trial Terms</Link>.
                You can cancel at any time before your trial ends.
              </p>
              <button
                type="button"
                onClick={() => setMode('password')}
                className="block mt-2 text-sm text-slate-600 hover:text-slate-900 underline"
                data-testid="signup-use-password"
              >
                Or sign up with a password instead
              </button>
            </form>
          )}

          {/* MAGIC LINK SENT — success state */}
          {mode === 'magic' && magicSent && (
            <div
              className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-6"
              data-testid="signup-magic-success"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                  <Check className="w-6 h-6 text-white" strokeWidth={3} />
                </div>
                <div className="flex-1">
                  <div className="font-extrabold text-slate-900 text-lg">Check your inbox 📥</div>
                  <p className="mt-1 text-slate-700 text-sm">
                    We sent a one-click sign-in link to <b>{email}</b>. The link expires in 15 minutes.
                  </p>
                  <p className="mt-3 text-xs text-slate-500">
                    Don't see it? Check Promotions / Spam, or{' '}
                    <button
                      type="button"
                      onClick={() => setMagicSent(false)}
                      className="underline text-orange-600 font-semibold"
                      data-testid="signup-magic-retry"
                    >try a different email</button>.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* CLASSIC PASSWORD MODE */}
          {mode === 'password' && (
            <form onSubmit={handlePasswordSignup} className="space-y-3" data-testid="signup-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="email-pw">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email-pw"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourbusiness.co.uk"
                    className="pl-10 h-12"
                    data-testid="signup-email-input-pw"
                    autoComplete="email"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="pl-10 pr-10 h-12"
                    data-testid="signup-password-input"
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-lg group"
                data-testid="signup-password-submit"
              >
                {loading ? 'Creating…' : (<>Start free — make my first ad <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" /></>)}
              </Button>
              <p className="text-xs text-slate-500 leading-relaxed">
                By continuing you agree to our
                {' '}<Link to="/legal/terms" target="_blank" className="text-orange-600 font-semibold hover:underline">Terms</Link>,
                {' '}<Link to="/legal/privacy" target="_blank" className="text-orange-600 font-semibold hover:underline">Privacy Policy</Link> and
                {' '}<Link to="/legal/trial-terms" target="_blank" className="text-orange-600 font-semibold hover:underline">Free Trial Terms</Link>.
              </p>
              <button
                type="button"
                onClick={() => setMode('magic')}
                className="block mt-2 text-sm text-slate-600 hover:text-slate-900 underline"
                data-testid="signup-use-magic"
              >
                ← Use a one-click email link instead
              </button>
            </form>
          )}

          <p className="mt-7 text-sm text-slate-600 text-center">
            Already have an account? <Link to="/login" className="font-semibold text-orange-600 hover:underline">Sign in</Link>
          </p>
          <p className="mt-3 text-xs text-slate-400 text-center leading-relaxed">
            MyBusinessAdPro is a product of <strong className="text-slate-500">Krinal Ahir Ltd</strong> (Co. No. 11940942), registered in England &amp; Wales.
          </p>
        </div>
      </div>

      {/* RIGHT PANEL — social proof, replaces the static "Your shop. Their feed." */}
      <div className="hidden lg:block flex-1 relative bg-gradient-to-br from-orange-100 via-rose-100 to-amber-100">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-md space-y-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-orange-200 text-orange-700 text-xs font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" />
              UK ASA-compliant · No card to start
            </div>
            <h2 className="text-4xl xl:text-5xl font-extrabold text-slate-900 leading-tight">
              Trusted by <span className="bg-gradient-to-r from-orange-600 to-rose-600 bg-clip-text text-transparent">UK small businesses.</span>
            </h2>
            <div className="space-y-4">
              {(testimonials || []).slice(0, 3).map((t, i) => (
                <div key={i} className="bg-white/95 backdrop-blur rounded-2xl border border-white p-5 shadow-md" data-testid={`signup-testimonial-${i}`}>
                  <div className="flex gap-1 mb-2">
                    {Array.from({ length: t.rating || 5 }).map((_, j) => (
                      <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">"{t.quote}"</p>
                  <div className="mt-3 text-xs">
                    <span className="font-semibold text-slate-900">{t.name}</span>
                    <span className="text-slate-500"> · {t.business}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
